import Vue from 'vue';
import { createDashboard, deleteDashboard, getDashboards, getWidgets, updateDashboard } from '@/api/legacy/dashboards';
import { Logger } from '@/helpers/log';
import { ns } from '@/helpers/store';
import { combineFilters, naturalSort } from '@/helpers/util';
import { CREATE_DASHBOARD, DELETE_DASHBOARD, FETCH_ALL, FETCH_DASHBOARD, FETCH_DASHBOARDS, UPDATE_DASHBOARD } from '@/store/types/actions';
import { CURRENT_DASHBOARD, DASHBOARD_LIST_SCOPE, DASHBOARD_SCOPE } from '@/store/types/getters';
import { DASHBOARDS_FETCHED, DASHBOARD_CREATED, DASHBOARD_DELETED, DASHBOARD_FETCHED, DASHBOARD_REFRESH, DASHBOARD_SCOPE_SET, DASHBOARD_SLICER_CLEAR_VALUES, DASHBOARD_SLICER_SET, DASHBOARD_TIME_SET, DASHBOARD_UPDATED, WIDGETS_FETCHED } from '@/store/types/mutations';
import { WIDGETS } from '@/store/types/namespaces';
import currentDashboard from './getters/currentDashboard';

const logger = new Logger('store:amplify:dashboards');

const getters = {
  [CURRENT_DASHBOARD]: currentDashboard,
  [DASHBOARD_SCOPE](state) {
    const { filter, slicerProperty, slicerValues } = state;

    if (!filter && !slicerProperty) return null;

    const filters = [];
    if (filter) {
      filters.push(filter);
    }
    if (slicerProperty && slicerValues.length > 0) {
      const slices = slicerValues.map((v) => `${slicerProperty.field} == "${v}"`);
      const sliceExpression = slices.join(' || ');
      filters.push(sliceExpression);
    }

    return {
      expression: combineFilters(filters),
    };
  },
  [DASHBOARD_LIST_SCOPE](state) {
    const { filter, slicerProperty, slicerValues } = state;

    if (!filter && !slicerProperty) return null;

    const filters = [];
    if (filter) {
      filters.push(filter);
    }
    if (slicerProperty && slicerProperty.name !== '$id' && slicerValues.length > 0) {
      const slices = slicerValues.map((v) => `${slicerProperty.field} == "${v}"`);
      const sliceExpression = slices.join(' || ');
      filters.push(sliceExpression);
    }

    return {
      expression: combineFilters(filters),
    };
  },
};

const actions = {
  [FETCH_DASHBOARD]({ state, commit }, id) {
    // get the full dashboard and select it.
    const dashboard = state.dashboards.find(({ id: did }) => did === id);
    commit(DASHBOARD_FETCHED, dashboard);
    // Clear current slicer values
    commit(DASHBOARD_SLICER_CLEAR_VALUES);
    return Promise.resolve();
  },
  [FETCH_DASHBOARDS]({ commit, dispatch }) {
    return getDashboards()
      .then(({ restart, dashboards }) => {
        if (restart) {
          logger.warn('re-fetching to pickup new changes...');
          return dispatch(FETCH_ALL, {}, { root: true }).then(() => dispatch(FETCH_DASHBOARDS));
        }
        return getWidgets().then((widgets) => {
          commit(ns(WIDGETS, WIDGETS_FETCHED), widgets, { root: true });
          commit(DASHBOARDS_FETCHED, dashboards);
        });
      });
  },
  [CREATE_DASHBOARD]({ commit }) {
    // FIXME: assumes no widgets on new dashboard.
    const requested = { name: 'New Dashboard' };
    return createDashboard(requested)
      .then((result) => {
        commit(DASHBOARD_CREATED, result);
        return Promise.resolve(result);
      });
  },
  [UPDATE_DASHBOARD]({ commit }, { requested, preventLegacy }) {
    // FIXME: should not be called on widget update.
    return updateDashboard(requested, false, preventLegacy)
      .then((result) => {
        commit(DASHBOARD_UPDATED, result);
        return result;
      })
      .catch((err) => {
        if (err.response.data) {
          // FIXME: disable merge, but accept new.
          const { data } = err.response;
          commit(DASHBOARD_UPDATED, data.current);
          return Promise.reject({ current: data.current, response: err.response }); // eslint-disable-line prefer-promise-reject-errors, max-len
        }
        return Promise.reject(err);
      });
  },
  [DELETE_DASHBOARD]({ commit }, dashboard) {
    if (!dashboard || typeof dashboard !== 'object') {
      return Promise.reject(new Error('A dashboard must be passed in to delete'));
    }
    return deleteDashboard(dashboard).then(() => {
      commit(DASHBOARD_DELETED, dashboard);
    });
  },
};

const mutations = {
  [DASHBOARDS_FETCHED](state, dashboards) {
    state.dashboards = naturalSort(dashboards, 'name');
  },
  [DASHBOARD_FETCHED](state, dashboard) {
    state.dashboard = dashboard;
  },
  [DASHBOARD_CREATED](state, dashboard) {
    state.dashboard = dashboard;
    state.dashboards.push(dashboard);

    // Re-sort the list with the new added one
    state.dashboards = naturalSort(state.dashboards, 'name');
  },
  [DASHBOARD_UPDATED](state, dashboard) {
    state.dashboard = dashboard;
    const index = state.dashboards.findIndex(({ id }) => id === dashboard.id);
    if (index < 0) {
      logger.error('Unknown dashboard edited', dashboard);
      return;
    }
    Vue.set(state.dashboards, index, dashboard);
  },
  [DASHBOARD_DELETED](state, dashboard) {
    const index = state.dashboards.findIndex(({ id }) => id === dashboard.id);
    if (index < 0) {
      logger.error('Unknown dashboard deleted', dashboard);
      return;
    }
    state.dashboards.splice(index, 1);
  },
  [DASHBOARD_SCOPE_SET](state, filter) {
    state.filter = filter;
  },
  [DASHBOARD_SLICER_SET](state, { property, values }) {
    if (property) {
      state.slicerProperty = property;
      state.slicerValues = [];
    }
    if (values && Array.isArray(values)) {
      state.slicerValues = values;
      // Vue.set(state, 'slicerValues', values);
    }
  },
  [DASHBOARD_SLICER_CLEAR_VALUES](state) {
    state.slicerValues = [];
  },
  [DASHBOARD_TIME_SET](state, timePeriod) {
    state.timePeriod = timePeriod;
  },
  [DASHBOARD_REFRESH](state) {
    state.dashboardRefreshTime = Date.now();
  },
};

const state = {
  dashboards: [],
  dashboard: null,
  dashboardRefreshTime: null,
  scope: null,
  timePeriod: null,
  filter: null,
  slicerProperty: null,
  slicerValues: [],
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
